import React from 'react';
import { User, Mail, MapPin, Clock, ExternalLink, MessageSquare, History, Sparkles, Monitor, Globe } from 'lucide-react';

// Placeholder empty state
const EmptyProfile = () => (
    <div className="h-full flex flex-col items-center justify-center text-muted-foreground p-6 text-center">
        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
            <User size={32} className="text-muted-foreground/50" />
        </div>
        <p className="text-sm font-medium">Select a thread to view customer details.</p>
    </div>
);

function CustomerProfile({ thread, customer }) {
    if (!thread) return <EmptyProfile />;

    // Debug logging to confirm new version is loaded
    console.log('[CustomerProfile] V4 Robust Loaded', { thread, customer });

    // If we don't have a linked customer object yet (fetching...), show loading or fallback
    const name = customer?.name || "Guest User";
    const email = customer?.email || thread.customer_email || "No email captured";
    const location = customer?.location || "Unknown Location";
    const lastSeen = customer?.last_seen_at ? new Date(customer.last_seen_at).toLocaleString() : "Just now";
    const endUserId = customer?.id || thread.end_user_id;



    return (
        <div className="h-full overflow-y-auto bg-card/50">
            {/* Header */}
            <div className="p-8 flex flex-col items-center border-b border-border bg-card">
                <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center text-2xl font-bold text-primary mb-4 ring-4 ring-background shadow-sm">
                    {name.charAt(0).toUpperCase()}
                </div>
                <h2 className="text-lg font-bold text-foreground">{name}</h2>
                <div className="flex items-center gap-2 mt-2">
                    <span className="w-2 h-2 rounded-full bg-green-500"></span>
                    <span className="text-xs font-medium text-muted-foreground">Online</span>
                </div>

                <div className="mt-4">
                    <span className={`px-3 py-1 text-xs font-bold rounded-full border shadow-sm tracking-wide
                        ${(customer?.lifecycle_stage || 'cold').toLowerCase() === 'lead' ? 'bg-green-50 text-green-700 border-green-200' : ''}
                        ${(customer?.lifecycle_stage || 'cold').toLowerCase() === 'warm' ? 'bg-orange-50 text-orange-700 border-orange-200' : ''}
                        ${(customer?.lifecycle_stage || 'cold').toLowerCase() === 'cold' ? 'bg-gray-50 text-gray-600 border-gray-200' : ''}
                    `}>
                        {(customer?.lifecycle_stage || 'COLD').toUpperCase()}
                    </span>
                </div>

                <div className="flex gap-4 mt-6 w-full justify-center">
                    <button className="flex flex-col items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-primary transition-colors group">
                        <div className="w-8 h-8 rounded-full bg-muted group-hover:bg-primary/10 flex items-center justify-center transition-colors">
                            <Mail size={14} />
                        </div>
                        Email
                    </button>
                    <button className="flex flex-col items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-primary transition-colors group">
                        <div className="w-8 h-8 rounded-full bg-muted group-hover:bg-primary/10 flex items-center justify-center transition-colors">
                            <MessageSquare size={14} />
                        </div>
                        Message
                    </button>
                </div>
            </div>

            <div className="p-6 border-b border-border">
                <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-4 flex items-center gap-2">
                    <User size={12} />
                    About
                </h3>
                <div className="space-y-3">
                    <div className="flex items-center justify-between group">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Mail size={14} className="text-muted-foreground/50" />
                            <span>Email</span>
                        </div>
                        <span className="text-sm text-foreground truncate max-w-[150px]" title={email}>{email}</span>
                    </div>
                    <div className="flex items-center justify-between group">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <MapPin size={14} className="text-muted-foreground/50" />
                            <span>Location</span>
                        </div>
                        <span className="text-sm text-foreground truncate max-w-[150px]">
                            {(() => {
                                if (!thread.country) return location;
                                try {
                                    return new Intl.DisplayNames(['en'], { type: 'region' }).of(thread.country.toUpperCase());
                                } catch (e) {
                                    return thread.country;
                                }
                            })()}
                        </span>
                    </div>
                    <div className="flex items-center justify-between group">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            {thread.country ? (
                                <img src={`https://flagcdn.com/20x15/${thread.country.toLowerCase()}.png`}
                                    onError={(e) => e.target.style.display = 'none'}
                                    className="w-4 h-3 rounded-[2px]" alt={thread.country} />
                            ) : (
                                <Globe size={14} className="text-muted-foreground/50" />
                            )}
                            <span>IP Address</span>
                        </div>
                        <span className="text-sm text-foreground text-right">{thread.user_ip || 'Unknown'}</span>
                    </div>
                    <div className="flex items-center justify-between group">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Monitor size={14} className="text-muted-foreground/50" />
                            <span>Device</span>
                        </div>
                        <span className="text-sm text-foreground text-right" title={`${thread.device_type} - ${thread.os}`}>
                            {thread.device_type || 'Unknown'} <span className="text-xs text-muted-foreground">({thread.os || '?'})</span>
                        </span>
                    </div>
                    <div className="flex items-center justify-between group">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Globe size={14} className="text-muted-foreground/50" />
                            <span>Browser</span>
                        </div>
                        <span className="text-sm text-foreground text-right">{thread.browser || 'Unknown'}</span>
                    </div>
                    <div className="flex items-center justify-between group">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Clock size={14} className="text-muted-foreground/50" />
                            <span>Last Seen</span>
                        </div>
                        <span className="text-sm text-foreground text-right">{lastSeen}</span>
                    </div>
                </div>
            </div>

            {/* Visitor Persona - Split Badge Design */}
            <div className="p-6 border-b border-border">
                <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-4 flex items-center gap-2">
                    <Sparkles size={12} className="text-amber-500" />
                    Visitor Persona
                </h3>

                <div className="flex flex-col border border-border/60 rounded-lg overflow-hidden divide-y divide-border/60">
                    {customer?.metadata && Object.keys(customer.metadata).length > 0 ? (
                        Object.entries(customer.metadata)
                            //.filter(([_, value]) => !isValueEmpty(value)) // Hide empty values
                            .map(([key, value]) => {
                                // Format label
                                const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

                                // Detect data types and format
                                let displayContent = value;
                                let isLink = false;

                                // Helper filters for formatting
                                const formatString = (str) => str.replace(/_/g, ' ').trim();

                                if (Array.isArray(value)) {
                                    // It's already an array (native DB array)
                                    displayContent = value.map(formatString).join(', ');
                                } else if (typeof value === 'string') {
                                    // Check if it's a URL
                                    if (value.startsWith('http')) {
                                        isLink = true;
                                    }
                                    // Check if it's a JSON stringified array
                                    else if (value.startsWith('[') || value.includes(',')) {
                                        try {
                                            const parsed = JSON.parse(value);
                                            if (Array.isArray(parsed)) {
                                                displayContent = parsed.map(formatString).join(', ');
                                            }
                                        } catch (e) {
                                            // Fallback for simple comma separation if it's not valid JSON
                                            if (value.includes(',') && !value.includes('{')) {
                                                displayContent = value.split(',').map(formatString).join(', ');
                                            }
                                        }
                                    }
                                } else if (typeof value === 'object') {
                                    displayContent = JSON.stringify(value);
                                } else if (typeof value === 'boolean') {
                                    displayContent = value ? 'True' : 'False';
                                }

                                return (
                                    <div key={key} className="flex text-xs group hover:bg-muted/2 transition-colors">
                                        <div className="bg-muted/40 px-3 py-2 font-medium text-muted-foreground break-words border-r border-border/60 w-[180px] shrink-0 flex items-center select-none">
                                            {label}
                                        </div>
                                        <div className="bg-white/50 px-3 py-2 flex-1 flex items-center min-w-0">
                                            {isLink ? (
                                                <a
                                                    href={value}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-primary hover:underline hover:text-primary/80 truncate block w-full flex items-center gap-1.5"
                                                >
                                                    <ExternalLink size={10} className="shrink-0" />
                                                    <span className="truncate">{value}</span>
                                                </a>
                                            ) : (
                                                <span className="text-foreground block w-full leading-relaxed whitespace-pre-wrap break-words" title={typeof displayContent === 'string' ? displayContent : ''}>
                                                    {displayContent}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })
                    ) : (
                        <div className="text-center py-6 px-4 border border-dashed border-border/60 rounded-lg bg-muted/5">
                            <p className="text-xs text-muted-foreground">No persona data collected.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Journey */}
            <div className="p-6 border-b border-border">
                <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-4 flex items-center gap-2">
                    <History size={12} />
                    Journey
                </h3>
                {endUserId ? <JourneyHistory endUserId={endUserId} /> : <p className="text-xs text-muted-foreground">No history available</p>}
            </div>

            {/* Previous Tickets */}
            <div className="p-6">
                <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-4">Previous Tickets</h3>
                {endUserId ? <PreviousTickets endUserId={endUserId} currentThreadId={thread.thread_id} /> : <p className="text-xs text-muted-foreground">No previous tickets.</p>}
            </div>
        </div>
    );
}

// Sub-component for fetching previous tickets
const PreviousTickets = ({ endUserId, currentThreadId }) => {
    const [tickets, setTickets] = React.useState([]);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        if (!endUserId) return;

        setLoading(true);
        fetch(`/api/threads?endUserId=${endUserId}&status=resolved&limit=10`)
            .then(res => res.json())
            .then(data => {
                // Filter out current thread if it's resolved
                const filteredTickets = (data.threads || []).filter(
                    t => t.thread_id !== currentThreadId
                );
                setTickets(filteredTickets);
            })
            .catch(err => console.error(err))
            .finally(() => setLoading(false));
    }, [endUserId, currentThreadId]);

    if (loading) return (
        <div className="space-y-3">
            {[1, 2].map(i => (
                <div key={i} className="p-3 bg-muted/20 border border-border/50 rounded-lg">
                    <div className="h-3 w-3/4 bg-muted animate-pulse rounded mb-2"></div>
                    <div className="h-2 w-1/4 bg-muted/60 animate-pulse rounded"></div>
                </div>
            ))}
        </div>
    );
    if (tickets.length === 0) return <p className="text-xs text-muted-foreground">No previous tickets.</p>;

    return (
        <div className="space-y-3">
            {tickets.map((ticket) => (
                <div key={ticket.thread_id} className="p-3 bg-muted/30 border border-border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer">
                    <div className="text-xs text-foreground font-medium line-clamp-2 mb-1.5">{ticket.last_message_preview || 'No preview'}</div>
                    <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                        <Clock size={10} />
                        {new Date(ticket.last_message_at).toLocaleDateString()}
                    </div>
                </div>
            ))}
        </div>
    );
}

// Sub-component for fetching history
const JourneyHistory = ({ endUserId }) => {
    const [visits, setVisits] = React.useState([]);

    React.useEffect(() => {
        if (!endUserId) return;
        fetch(`/api/tracking/history/${endUserId}`)
            .then(res => res.json())
            .then(data => {
                if (data.visits) setVisits(data.visits);
            })
            .catch(err => console.error(err));
    }, [endUserId]);

    if (visits.length === 0) return <p className="text-xs text-muted-foreground">No pages visited yet.</p>;

    return (
        <div className="relative pl-3 border-l text-border space-y-4">
            {visits.map((visit, idx) => (
                <div key={visit.id || idx} className="relative pl-4">
                    <div className="absolute left-[-17px] top-1.5 w-2 h-2 rounded-full bg-border ring-4 ring-background"></div>
                    <div className="flex flex-col">
                        <span className="text-xs font-medium text-foreground">{visit.title || 'United Page'}</span>
                        <a href={visit.url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-primary hover:underline flex items-center gap-1 mt-0.5">
                            {new URL(visit.url).pathname} <ExternalLink size={8} />
                        </a>
                        <span className="text-[10px] text-muted-foreground mt-0.5">{new Date(visit.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default CustomerProfile;
